export interface Coordinates {
  latitude: number;
  longitude: number;
}

export function validCoordinates(value: unknown): value is Coordinates {
  const p = value as Coordinates | null;
  return (
    !!p &&
    Number.isFinite(p.latitude) &&
    Number.isFinite(p.longitude) &&
    Math.abs(p.latitude) <= 90 &&
    Math.abs(p.longitude) <= 180
  );
}

// Latitude first. Hemisphere letters support either prefix or suffix.
export function parseCoordinates(raw: string): Coordinates | null {
  const text = raw
    .trim()
    .toUpperCase()
    .replace(/O/g, 'W')
    .replace(/[º]/g, '°')
    .replace(/[′’]/g, "'")
    .replace(/[″”]/g, '"');
  const decimal = /^([+-]?\d+(?:\.\d+)?)\s*[,;\s]\s*([+-]?\d+(?:\.\d+)?)$/.exec(text);
  if (decimal) {
    const point = { latitude: Number(decimal[1]), longitude: Number(decimal[2]) };
    return validCoordinates(point) ? point : null;
  }
  const commaDecimal = /^([+-]?\d+,\d+)\s*;\s*([+-]?\d+,\d+)$/.exec(text);
  if (commaDecimal)
    return parseCoordinates(
      `${commaDecimal[1]!.replace(',', '.')};${commaDecimal[2]!.replace(',', '.')}`,
    );
  const suffix = /^(.+?[NS])\s*[,;/]?\s*(.+?[EW])$/.exec(text);
  const prefix = /^([NS].+?)\s*[,;/]?\s*([EW].+)$/.exec(text);
  const pair = suffix ?? prefix;
  if (!pair) return null;
  const angle = (part: string, latitude: boolean): number | null => {
    const hemisphere = part.match(/[NSEW]/g);
    if (hemisphere?.length !== 1 || !(latitude ? /[NS]/ : /[EW]/).test(hemisphere[0])) return null;
    const clean = part
      .replace(/[NSEW]/g, '')
      .trim()
      .replace(/,/g, '.');
    if (!/^[\d.\s°'":]+$/.test(clean)) return null;
    let pieces = clean.split(/[\s°'":]+/).filter(Boolean);
    const digits = latitude ? 2 : 3;
    if (pieces.length === 1 && new RegExp(`^\\d{${digits + 4}}(?:\\.\\d+)?$`).test(clean))
      pieces = [clean.slice(0, digits), clean.slice(digits, digits + 2), clean.slice(digits + 2)];
    else if (pieces.length === 1 && new RegExp(`^\\d{${digits + 2}}(?:\\.\\d+)?$`).test(clean))
      pieces = [clean.slice(0, digits), clean.slice(digits)];
    if (!pieces.length || pieces.length > 3 || pieces.some((p) => !/^\d+(?:\.\d+)?$/.test(p)))
      return null;
    const [degrees = 0, minutes = 0, seconds = 0] = pieces.map(Number);
    if (
      minutes >= 60 ||
      seconds >= 60 ||
      (pieces.length > 1 && !Number.isInteger(degrees)) ||
      (pieces.length > 2 && !Number.isInteger(minutes))
    )
      return null;
    return (degrees + minutes / 60 + seconds / 3600) * (/[SW]/.test(part) ? -1 : 1);
  };
  const latitude = angle(pair[1]!, true),
    longitude = angle(pair[2]!, false);
  const point = { latitude, longitude };
  return validCoordinates(point) ? point : null;
}

export function straightLineDistance(a: Coordinates, b: Coordinates) {
  const rad = (n: number) => (n * Math.PI) / 180;
  const h =
    Math.sin(rad(b.latitude - a.latitude) / 2) ** 2 +
    Math.cos(rad(a.latitude)) *
      Math.cos(rad(b.latitude)) *
      Math.sin(rad(b.longitude - a.longitude) / 2) ** 2;
  const km = 6371.0088 * 2 * Math.asin(Math.sqrt(Math.min(1, Math.max(0, h))));
  return { km, nm: km / 1.852 };
}
